/**
 * components/KelolaCabangModal.js
 * Manage the cabang branches list: add new, rename, delete (only when unreferenced by any
 * salesperson or transaction), and edit each branch's own bank account. Built directly off
 * KelolaSalesModal's skeleton — the closest structural match (add panel + inline edit row +
 * typed-"HAPUS" delete). Opened from Pengaturan's own "Kelola Cabang" button (moved there from
 * Penjualan.js's page header — branch management is app-level settings, not a Penjualan-page
 * concern) — not a standalone nav page, and not opened from inside TransactionFormModal.
 *
 * **`bankAccount`** (per-location bank account): a plain free-text textarea, alongside Nama
 * Cabang in both the add panel and the edit row. Optional, may be empty — never validated,
 * reformatted, or compared against another location's account (see `utils/branches.js`'s own
 * header comment). Commits on the SAME "Tambah"/"Simpan" click as the name, immediately —
 * there is no separate save step for it. Pusat's own account lives elsewhere entirely
 * (`data.settings.bankAccount`, edited in Pengaturan's company form) — this modal only ever
 * edits real `data.branches` records, so Pusat never appears in it, same as before.
 *
 * **REKENING BANK is also its own table column** (second, right after NAMA CABANG — Sales/
 * Transaksi shift right, Aksi stays last), so reading a branch's account never requires opening
 * Ubah. Rendered IN FULL: `white-space: pre-wrap` keeps real line breaks and whitespace exactly
 * as stored (no truncation, no ellipsis, no hover-only text — a stored value must be visible on
 * screen, and hover doesn't exist on a phone), so a row's height simply grows with its content.
 * An empty account shows a greyed "belum diisi" placeholder rather than a blank cell, so an
 * unset value reads as "genuinely unset," not a rendering fault. Ubah is unaffected — it still
 * opens the same edit row as before; this column only removes the need to open it to LOOK.
 *
 * **Fitting the wider table (5 columns) inside the modal**: the new REKENING BANK column pushed
 * the table past a plain 560px `.modal-box` under the table's default `table-layout: auto` —
 * every column, including Aksi, sizes to its widest line of content under `auto`, and
 * `white-space: pre-wrap` alone doesn't bound a column's width (it preserves line breaks, but a
 * long unbroken line or word still stretches the column that holds it). Three options were on
 * the table: (1) a horizontally scrollable wrapper, (2) a wider modal, (3) forcing the free-text
 * columns to wrap within a bounded width instead of sizing to their content. **Chose (2) + (3)
 * together** — the same combination `KelolaSalesModal` already used for an identical problem
 * (see `.kelola-roster-table`'s own comment in styles.css): `.modal-box--wide` (760px) for
 * breathing room, plus `table-layout: fixed` (`.kelola-cabang-table`) with an explicit
 * `<colgroup>` so Sales/Transaksi/Aksi get real guaranteed widths and Nama Cabang/Rekening Bank
 * share the rest — `overflow-wrap: anywhere` on both of those two cells (`.kelola-cabang-wrap`)
 * so even a single long unbroken token (a long branch name, an account number with no spaces)
 * wraps inside its own column instead of stretching it. **Not (1)**: `.table-wrap`'s default
 * `overflow-x: auto` is deliberately overridden to `visible` app-wide for every "Kelola X" modal
 * (`.kelola-table-wrap`, styles.css) specifically because the disabled-Hapus `.tip` tooltip is an
 * absolutely-positioned pseudo-element that a scrolling ancestor would clip — reintroducing
 * scroll here to fix THIS overflow would silently reopen THAT one. Verified against a real
 * headless-Chrome render (this project's own standard for layout claims jsdom can't prove —
 * jsdom computes no real layout at all) with a three-line bank account and a long branch name
 * together in the same table: nothing overflows `.modal-box`, both wrap in place, row height
 * grows to fit. jsdom's own coverage here (KelolaCabangModal.test.js) only guards the mechanism
 * — the classes/colgroup exist — same "regression guard, not proof" caveat the print components
 * already carry for their own real-print-verified geometry.
 */
import React, { useState } from "react";
import { useModalEsc, backdropClose } from "../utils/modalStack";
import { branchRefCount } from "../utils/branches";
import DeleteConfirmModal from "./DeleteConfirmModal";
import { PencilIcon, TrashIcon } from "./Icons";

export default function KelolaCabangModal({ branches, salespeople, transactions, onAdd, onUpdate, onDelete, onClose }) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newBankAccount, setNewBankAccount] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editBankAccount, setEditBankAccount] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null); // branch object

  useModalEsc(onClose);

  const nameUsed = (name, excludeId) =>
    branches.some((b) => b.id !== excludeId && b.name.trim().toLowerCase() === name.trim().toLowerCase());

  // ── Add panel ──────────────────────────────────────────────────────────────
  const nameAdd = newName.trim();
  // Disabled-control sweep (manual-testing report): a blank name used to return "" here too —
  // same as "no error," so Tambah sat disabled with nothing explaining why. Now flows through
  // this exact same existing `{nameErrorAdd && ...}` hint below, no new JSX needed.
  const nameErrorAdd = !nameAdd ? "Wajib diisi." : nameUsed(nameAdd, null) ? "Nama cabang sudah dipakai." : "";

  const collapseAdd = () => { setAdding(false); setNewName(""); setNewBankAccount(""); };
  const handleTambah = () => {
    if (!nameAdd || nameErrorAdd) return;
    onAdd({ name: nameAdd, bankAccount: newBankAccount });
    collapseAdd();
  };

  // ── Edit panel ────────────────────────────────────────────────────────────
  const openEdit = (b) => { collapseAdd(); setEditingId(b.id); setEditName(b.name); setEditBankAccount(b.bankAccount || ""); };
  const collapseEdit = () => { setEditingId(null); setEditName(""); setEditBankAccount(""); };

  const nameEdit = editName.trim();
  // Disabled-control sweep (manual-testing report): same fix as nameErrorAdd above.
  const nameErrorEdit = !nameEdit ? "Wajib diisi." : nameUsed(nameEdit, editingId) ? "Nama cabang sudah dipakai." : "";
  const editValid = !!nameEdit && !nameErrorEdit;

  const handleSimpan = () => {
    if (!editValid) return;
    onUpdate(editingId, { name: nameEdit, bankAccount: editBankAccount });
    collapseEdit();
  };

  return (
    <>
      <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={backdropClose(onClose)}>
        <div className="modal-box modal-box--wide" onMouseDown={(e) => e.stopPropagation()}>
          <h2 className="modal-title">Kelola Cabang</h2>
          <div className="page-sub" style={{ marginBottom: "var(--gap-lg)" }}>
            Tambah, ubah, atau hapus cabang. Setiap sales dan transaksi cabang tercatat di salah satu cabang ini.
          </div>

          {!adding && (
            <div style={{ marginBottom: "var(--gap-lg)" }}>
              <button type="button" className="btn btn-soft" onClick={() => { collapseEdit(); setAdding(true); }}>
                <span className="btn-circle-plus" aria-hidden="true">+</span> Tambah Cabang
              </button>
            </div>
          )}
          {adding && (
            <div style={{ border: "1px solid var(--border)", borderRadius: 6, padding: 12, marginBottom: "var(--gap-lg)" }}>
              <div className="form-row">
                <label className="form-label" htmlFor="kelola-cabang-add-nama">Nama Cabang</label>
                <input id="kelola-cabang-add-nama" className="form-input" value={newName} autoFocus onChange={(e) => setNewName(e.target.value)} />
                {nameErrorAdd && <div className="form-hint" style={{ color: "var(--red)" }}>{nameErrorAdd}</div>}
              </div>
              <div className="form-row">
                <label className="form-label" htmlFor="kelola-cabang-add-rekening">Rekening Bank</label>
                <textarea id="kelola-cabang-add-rekening" className="form-input" rows={2} value={newBankAccount}
                  onChange={(e) => setNewBankAccount(e.target.value)} />
                <div className="form-hint">Opsional. Teks bebas, disimpan persis seperti diketik.</div>
              </div>
              <div className="modal-actions" style={{ marginTop: "var(--gap-sm)" }}>
                <button type="button" className="btn" onClick={collapseAdd}>Batal</button>
                <button type="button" className="btn btn-primary"
                  disabled={!nameAdd || !!nameErrorAdd} onClick={handleTambah}>Tambah</button>
              </div>
            </div>
          )}

          <div className="table-wrap kelola-table-wrap">
            <table className="table kelola-cabang-table">
              <colgroup>
                <col style={{ width: 150 }} />
                <col />
                <col style={{ width: 70 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 190 }} />
              </colgroup>
              <thead><tr><th>Nama Cabang</th><th>Rekening Bank</th><th>Sales</th><th>Transaksi</th><th>Aksi</th></tr></thead>
              <tbody>
                {branches.length === 0 ? (
                  <tr><td colSpan={5} className="empty-state">Belum ada cabang.</td></tr>
                ) : branches.map((b) => {
                  const salesCount = salespeople.filter((s) => s.branchId === b.id).length;
                  const txnCount = transactions.filter((t) => t.branchId === b.id).length;
                  const count = branchRefCount({ salespeople, transactions }, b.id);
                  const isEditing = editingId === b.id;
                  return (
                    <React.Fragment key={b.id}>
                      <tr style={{ cursor: "default" }}>
                        <td className="kelola-cabang-wrap">{b.name}</td>
                        <td className="kelola-cabang-wrap" style={{ whiteSpace: "pre-wrap" }}>
                          {b.bankAccount ? b.bankAccount : <span style={{ color: "var(--muted)" }}>belum diisi</span>}
                        </td>
                        <td>{salesCount}</td>
                        <td>{txnCount}</td>
                        <td className="row-actions">
                          <button className="btn btn-sm btn-ghost" onClick={() => (isEditing ? collapseEdit() : openEdit(b))}>
                            <PencilIcon /> Ubah
                          </button>
                          <span className="tip" {...(count > 0 ? { "data-tip": `Dipakai di ${salesCount} sales / ${txnCount} transaksi — tidak bisa dihapus.` } : {})}>
                            <button className="btn btn-sm btn-danger-ghost" disabled={count > 0}
                              onClick={() => setDeleteTarget(b)}>
                              <TrashIcon /> Hapus
                            </button>
                          </span>
                        </td>
                      </tr>
                      {isEditing && (
                        <tr>
                          <td colSpan={5} style={{ background: "#f8fafc" }}>
                            <div style={{ padding: "10px 4px" }}>
                              <div className="form-row">
                                <label className="form-label" htmlFor="kelola-cabang-edit-nama">Nama Cabang</label>
                                <input id="kelola-cabang-edit-nama" className="form-input" value={editName} autoFocus
                                  onChange={(e) => setEditName(e.target.value)} />
                                {nameErrorEdit && <div className="form-hint" style={{ color: "var(--red)" }}>{nameErrorEdit}</div>}
                              </div>
                              <div className="form-row">
                                <label className="form-label" htmlFor="kelola-cabang-edit-rekening">Rekening Bank</label>
                                <textarea id="kelola-cabang-edit-rekening" className="form-input" rows={2} value={editBankAccount}
                                  onChange={(e) => setEditBankAccount(e.target.value)} />
                                <div className="form-hint">Opsional. Teks bebas, disimpan persis seperti diketik.</div>
                              </div>
                              <div className="modal-actions" style={{ marginTop: "var(--gap-sm)" }}>
                                <button type="button" className="btn" onClick={collapseEdit}>Batal</button>
                                <button type="button" className="btn btn-primary" disabled={!editValid}
                                  onClick={handleSimpan}>Simpan</button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="modal-actions" style={{ marginTop: "var(--gap-lg)" }}>
            <button type="button" className="btn btn-primary" onClick={onClose}>Tutup</button>
          </div>
        </div>
      </div>

      {deleteTarget && (
        <DeleteConfirmModal
          title="Hapus cabang?"
          message={<>Anda akan menghapus cabang <b>{deleteTarget.name}</b>. Tindakan ini permanen.</>}
          requireText="HAPUS"
          onConfirm={() => { onDelete(deleteTarget.id); setDeleteTarget(null); }}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </>
  );
}
